import { humanise, isPascalCase } from "@util/string.util";
import { ApolloError } from "@apollo/client";

export type ParsedErrorType =
  | "exclusivity"
  | "validation"
  | "unknown"
  | "unauthorised"
  | "notfound";

export class ParsedError extends Error {
  type: ParsedErrorType;
  constructor(message: string, type: ParsedErrorType, originalError: any) {
    super(message);
    this.stack = originalError.stack;
    this.type = type;
  }
}

export enum APIErrorCode {
  INPUT_NOT_UNIQUE = "INPUT_NOT_UNIQUE",
  BAD_USER_INPUT = "BAD_USER_INPUT",
  NOT_FOUND = "NOT_FOUND",
}

export function parseServerError(error: unknown): ParsedError {
  if (!(error instanceof Error) && !(error instanceof ApolloError)) {
    return new ParsedError(
      "Unknown error, please try again.",
      "unknown",
      error
    );
  }

  // Exclusivity contraints, formatted like "batch_number violates exclusivity constraint"
  // We want to extract "Batch number" from this error message as it's more user-friendly
  if (error.message.includes("violates exclusivity constraint")) {
    const entity = error.message.split(" ")[0];

    // Make a little assumption here that if the entity is in PascalCase, it's an exclusively named entity that
    // has triggered the exclusivity constraint. This relies on our schema naming convention specifying that entities
    // are in PascalCase and their fields are in snake_case. This is a safe assumption to make as it's our convention,
    // but does rely on us maintaining that convention when making schema changes!
    // Given that assumption, if the entity is in PascalCase it means that the name is already in use. Otherwise, it is
    // some particular field that is already in use.
    const isExclusivelyNamedEntity = isPascalCase(entity);
    return new ParsedError(
      `The ${isExclusivelyNamedEntity ? "name of the " : ""}${humanise(
        entity
      )} you entered is already in use.`,
      "exclusivity",
      error
    );
  }

  if (error.message.startsWith("Access denied!")) {
    return new ParsedError("Unauthorised action.", "unauthorised", error);
  }

  if (
    error instanceof ApolloError &&
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0].extensions?.code
  ) {
    switch (error.graphQLErrors[0].extensions.code) {
      case APIErrorCode.INPUT_NOT_UNIQUE:
        return new ParsedError(error.message, "exclusivity", error);
      case APIErrorCode.BAD_USER_INPUT:
        return new ParsedError(error.message, "validation", error);
      case APIErrorCode.NOT_FOUND:
        return new ParsedError(error.message, "notfound", error);
    }
  }

  return new ParsedError("Unknown error, please try again.", "unknown", error);
}
